import { useEffect, useState } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { postTenantDepartmentData } from "@services/API/Requests/TenantDepartmentRequests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BlankTenantDepartmentData } from "@templates/BlankTenantDepartmentData";
import { TENANTDEPARTMENT } from "@types/tenantDepartment";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { error_message } from "@constants/Errors";
import { KAKELY_PATHS } from "@constants/NavigationPaths";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getTenantDepartmentData } from "../../../../services/API/Requests/TenantDepartmentRequests";

export const useCreateDepartmentGroupPage = () => {
  const navigate: NavigateFunction = useNavigate();
  const { parentId } = useParams<{ parentId: string }>();
  const location = useLocation();
  const [parentCode, setParentCode] = useState<string | undefined>(
    location.state?.parentCode,
  );
  const isCreateChild = parentId && parentId !== "Master";

  const tempUserId = useSelector((state) => state.userForm.tempUserId);

  const isNotMaster: string | null =
    parentId && parentId !== "Master" ? parentId : null;

  const [department, setDepartment] = useState<TENANTDEPARTMENT | null>(
    BlankTenantDepartmentData,
  );

  const [parentDepartment, setParentDepartment] =
    useState<TENANTDEPARTMENT | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const errorToast = (response) => toast.error(`${response}`);
  const successToast = (response) => toast.success(`${response}`);

  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);

  const [errors, setErrors] = useState({
    name: false,
    name_kana: false,
  });

  useEffect(() => {
    if (isCreateChild) {
      setParentCode(parentCode || parentDepartment?.display_order);
    }
  }, [isCreateChild, parentCode, parentDepartment]);

  useEffect(() => {
    if (!isCreateChild) return;
    const fetchDepartmentData = async () => {
      try {
        setLoading(false);
        await getTenantDepartmentData(
          setParentDepartment,
          null,
          setError,
          parentId || "0",
        );
      } catch (err) {
        setError("親部署の情報の取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentData();
  }, [isCreateChild]);

  useEffect(() => {
    setDepartment(BlankTenantDepartmentData);
    if (parentCode) {
      setDepartment((prevState) =>
        prevState
          ? {
              ...prevState,
              parent_code: parentCode || parentDepartment?.display_order,
            }
          : prevState,
      );
    }
  }, [parentCode]);

  const handleInputChange = (key: string, value: string) => {
    setDepartment((prevState) =>
      prevState ? { ...prevState, [key]: value } : prevState,
    );
  };

  const verifyFields = () => {
    return !Object.values(errors).some((error) => error === true);
  };

  const updateError = (field, value) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: value,
    }));
  };

  const handleSave = async (event) => {
    setError("");
    setLoading(true);
    event.preventDefault();
    const fields = verifyFields();

    if (!fields) {
      errorToast(error_message.generic.bad_field);
      setLoading(false);
      return;
    }

    if (department) {
      if (isNotMaster) {
        department.parent = parentId;
      }
      let response;
      await postTenantDepartmentData(
        setDepartment,
        department,
        setLoading,
        setError,
      )
        .then((response) => {
          if (response.status == 200) {
            successToast(response.message);
            setTimeout(() => {
              if (tempUserId) {
                navigate("/users/" + tempUserId);
              } else {
                navigate(KAKELY_PATHS.allDepartmentSettings);
              }
            }, 1000);
          } else {
            const errorMessage = TranslateErrorMessage(response.message);
            console.error(errorMessage);
            setError(errorMessage);
            errorToast(errorMessage);
          }
        })
        .catch((error) => {
          const errorMessage = TranslateErrorMessage(error.message);
          console.error(errorMessage);
          setError(errorMessage);
          errorToast(errorMessage);
        });
    }
  };

  const onCancel = () => {
    if (tempUserId) {
      navigate("/users/" + tempUserId);
    } else {
      navigate(KAKELY_PATHS.allDepartmentSettings);
    }
  };

  const handleOpenWarningModal = () => {
    setWarningModalOpen(true);
  };

  const handleCloseWarningModal = () => {
    setWarningModalOpen(false);
  };
  return {
    department,
    warningModalOpen,
    error,
    errors,
    isNotMaster,
    loading,
    parentCode,
    isCreateChild,
    onCancel,
    handleInputChange,
    handleOpenWarningModal,
    handleSave,
    handleCloseWarningModal,
    updateError,
  };
};
